import { defineQuery } from '@rocicorp/zero';
import * as z from 'zod';
import { CompoundMappingType, CompoundOrderStatus } from '../database/schema.js';
import { zql } from '../schema.js';
import { Status } from '../util/schemas.js';
import { applyCursor } from '../util/apply-cursor.js';
import { applyPopulate, createPopulateSchema } from './populates.js';
import { Cursor, Sort } from './util.js';
const CompoundOrderPopulateSchema = createPopulateSchema('full');
export const ListCompoundOrdersInput = z.object({
    status: CompoundOrderStatus.optional(),
    populate: CompoundOrderPopulateSchema,
    cursor: Cursor.extend({
        createdAt: z.number(),
    }).optional(),
    sort: Sort.extend({
        field: z.enum(['createdAt']).default('createdAt'),
    }).optional(),
    limit: z.number().optional(),
});
export const ListCompoundOrdersBySequenceInput = z.object({
    sequenceId: z.string(),
    populate: CompoundOrderPopulateSchema,
});
export const ListCompoundOrdersByOrderInput = z.object({
    orderId: z.string(),
    populate: CompoundOrderPopulateSchema,
});
export const ListCompoundMappingsInput = z.object({
    mappingType: CompoundMappingType.optional(),
    status: Status.optional(),
});
/**
 * Populate profiles for compound order queries.
 *
 * - `minimal`: No relations.
 * - `list`: The preparation result (with its components).
 * - `full`: The therapy sequence (with patient), the production order and the result.
 */
export const compoundOrderPopulates = {
    minimal: null,
    list: q => q.related('result', r => r.related('components').where('deletedAt', 'IS', null)),
    full: q => q
        .related('therapySequence', s => s.related('patient'))
        .related('order')
        .related('result', r => r.related('components').where('deletedAt', 'IS', null)),
};
export const compoundersQueries = {
    getCompoundOrderById: defineQuery(z.string(), ({ args }) => {
        let q = zql.compoundOrders.where('id', args).where('deletedAt', 'IS', null);
        q = applyPopulate(q, compoundOrderPopulates, 'full');
        return q.one();
    }),
    listCompoundOrdersBySequence: defineQuery(ListCompoundOrdersBySequenceInput, ({ args }) => {
        let q = zql.compoundOrders.where('therapySequenceId', '=', args.sequenceId).where('deletedAt', 'IS', null);
        q = applyPopulate(q, compoundOrderPopulates, args.populate);
        return q.orderBy('createdAt', 'desc');
    }),
    listCompoundOrdersByOrder: defineQuery(ListCompoundOrdersByOrderInput, ({ args }) => {
        let q = zql.compoundOrders.where('orderId', '=', args.orderId).where('deletedAt', 'IS', null);
        q = applyPopulate(q, compoundOrderPopulates, args.populate);
        return q.orderBy('createdAt', 'desc');
    }),
    listCompoundOrders: defineQuery(ListCompoundOrdersInput, ({ args }) => {
        let base = zql.compoundOrders.where('deletedAt', 'IS', null);
        if (args.status)
            base = base.where('status', '=', args.status);
        const q = applyPopulate(base, compoundOrderPopulates, args.populate);
        return applyCursor(q, { sort: args.sort, cursor: args.cursor, limit: args.limit });
    }),
    listCompoundMappings: defineQuery(ListCompoundMappingsInput, ({ args }) => {
        let q = zql.compoundMappings.where('deletedAt', 'IS', null);
        if (args.mappingType)
            q = q.where('mappingType', '=', args.mappingType);
        if (args.status)
            q = q.where('status', '=', args.status);
        return q.orderBy('mappingType', 'asc').orderBy('createdAt', 'desc');
    }),
};
//# sourceMappingURL=compounders.queries.js.map